import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LOGO, TG_LOGO, testguardHref, campuspathHref } from "@/lib/constants";
import { TGWordmark } from "@/components/TGWordmark";
import CTA from "@/components/CTA";

const BASE_LINKS = [
  { label: "Platform", to: "/#platform" },
  { label: "Security", to: "/#security" },
  { label: "Stories", to: "/#stories" },
];

export default function SiteNav({ dark = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const LINKS = [
    ...BASE_LINKS,
    dark
      ? { label: "CampusPath", to: campuspathHref(), external: true }
      : { label: "TestGuard", to: testguardHref(), external: true },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const glass = dark
    ? "bg-obsidian/70 border-white/10 text-white"
    : "bg-bone/70 border-ink/10 text-ink";
  const textColor = dark ? "text-white/70 hover:text-leaf" : "text-ink/60 hover:text-ink";

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-4 pt-4 md:px-8">
      <nav
        className={`mx-auto flex max-w-7xl items-center justify-between rounded-full border px-5 py-2.5 backdrop-blur-2xl transition-shadow duration-300 ${glass} ${
          scrolled ? "shadow-[0_10px_40px_-20px_rgba(0,34,68,0.4)]" : ""
        }`}
        data-testid="site-nav"
      >
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img
            src={dark ? TG_LOGO : LOGO}
            alt={dark ? "TestGuard" : "CampusPath"}
            className="h-12 w-auto object-contain"
          />
          {dark ? (
            <TGWordmark className="font-display text-lg font-800 tracking-tight" />
          ) : (
            <span className="font-display text-lg font-800 tracking-tight text-ink">
              Campus<span className="text-leaf">Path</span>
            </span>
          )}
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.to}
              className={`text-sm font-500 tracking-tight transition-colors duration-200 ${textColor}`}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/demo"
            className={`text-sm font-600 tracking-tight transition-colors duration-200 ${textColor}`}
            data-testid="nav-demo-link"
          >
            Book a demo
          </Link>
          <CTA to="/trial" variant={dark ? "leafDark" : "leaf"} className="!px-5 !py-2.5" dataTestId="nav-trial-btn">
            Free Trial
          </CTA>
        </div>

        <button
          className={`lg:hidden ${dark ? "text-white" : "text-ink"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path d={open ? "M6 6l12 12M18 6L6 18" : "M4 7h16M4 12h16M4 17h16"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </nav>

      {open && (
        <div className={`mx-auto mt-2 max-w-7xl rounded-3xl border p-5 backdrop-blur-2xl lg:hidden ${glass}`}>
          <div className="flex flex-col gap-4">
            {LINKS.map((l) => (
              <a key={l.label} href={l.to} className="text-base font-600" data-testid={`nav-m-${l.label.toLowerCase()}`}>
                {l.label}
              </a>
            ))}
            <Link to="/demo" className="text-base font-600">Book a demo</Link>
            <CTA to="/trial" variant="leaf" className="justify-center">Free Trial</CTA>
          </div>
        </div>
      )}
    </header>
  );
}
